import { useEffect, useCallback, useState } from "react";
import type { MessagePayload, Unsubscribe } from "firebase/messaging";
import { getMessagingInstance } from '../services/firebase';
import { toast } from "react-hot-toast";
import showSystemNotification from "../utils/showSystemNotification";

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

const useFirebaseNotifications = () => {
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );

  const requestPermission = useCallback(async () => {
    try {
      const messaging = await getMessagingInstance();
      if (!messaging) return false;

      if (typeof Notification === "undefined") {
        setPermissionStatus("default");
        return false;
      }

      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);

      if (permission === 'granted') {
        const { getToken } = await import("firebase/messaging");
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        if (token) {
          console.log("🔥 FCM Token:", token);
          // Here we would sync with backend/firestore if needed
          localStorage.setItem('fcm_token_registered', 'true');
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error("FCM Permission request failed", error);
      return false;
    }
  }, []);

  useEffect(() => {
    let unsubscribe: Unsubscribe | null = null;

    const setupNotifications = async (): Promise<void> => {
      const messaging = await getMessagingInstance();
      if (!messaging) {
        console.log("🔕 Notifications non supportées.");
        return;
      }

      const { onMessage } = await import("firebase/messaging");
      unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
        console.log("📩 Notification reçue :", payload);
        const title = payload.notification?.title ?? payload.data?.title ?? "Jacana Nature";
        const body = payload.notification?.body ?? payload.data?.body ?? "Nouvelle mise à jour sauvage.";

        toast.success(`${title}: ${body}`, {
          icon: '🌿',
          style: {
            background: '#F4EFE6',
            color: '#000',
            border: '1px solid rgba(0,0,0,0.05)',
            fontFamily: 'serif',
            fontStyle: 'italic',
          }
        });

        if (!payload.notification) {
          showSystemNotification(title, body);
        }
      });
    };

    if (permissionStatus === 'granted') {
      void setupNotifications().catch((error) => {
        console.error("FCM listener setup failed", error);
      });
    }

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [permissionStatus]);

  return { permissionStatus, requestPermission };
};

export default useFirebaseNotifications;
